import React, { useState, useEffect } from 'react';
import './RecapLinkModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';

/**
 * FreightRecapLinkModal
 *
 * Link one or more ACTIVE freight recaps to a delivery (Jonas feedback
 * 19.08: attaching freight was only possible while creating a delivery).
 * Deliberately separate from RecapLinkModal: freight recaps carry no
 * product/currency/unit, so the trade-leg compatibility rules and partial
 * allocation UI do not apply. Styling reuses RecapLinkModal.css.
 *
 * Props:
 *  - isOpen, onClose
 *  - availableRecaps: freight recaps from /api/deliveries/available-recaps
 *  - linkedNumbers: transaction numbers already linked (shown as such)
 *  - onLink(selectedRecaps): called with the selected recap objects
 */
const FreightRecapLinkModal = ({
  isOpen,
  onClose,
  availableRecaps = [],
  linkedNumbers = [],
  onLink,
}) => {
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setSelected([]);
  }, [isOpen]);

  if (!isOpen) return null;

  const toggle = (recap) => {
    setSelected((prev) =>
      prev.some((r) => r.transaction_number === recap.transaction_number)
        ? prev.filter((r) => r.transaction_number !== recap.transaction_number)
        : [...prev, recap]
    );
  };

  const handleLink = async () => {
    if (selected.length === 0) return;
    setIsLoading(true);
    try {
      await onLink(selected);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const selectable = availableRecaps.filter(
    (r) => !linkedNumbers.includes(r.transaction_number)
  );

  return (
    <div className="recap-link-modal-overlay" onClick={onClose}>
      <div className="recap-link-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="recap-link-modal-header">
          <h2>Link Freight Recap</h2>
          <button type="button" className="recap-link-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="recap-link-modal-body">
          {selectable.length === 0 ? (
            <p style={{ padding: '12px 4px', color: '#666' }}>
              No active freight recaps available to link.
            </p>
          ) : (
            <div className="recap-link-list">
              {selectable.map((recap) => {
                const isSelected = selected.some(
                  (r) => r.transaction_number === recap.transaction_number
                );
                return (
                  <div
                    key={recap.transaction_number}
                    className={`recap-link-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggle(recap)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="recap-link-item-checkbox">
                      <FontAwesomeIcon
                        icon={isSelected ? faCheckSquare : faSquare}
                        className="recap-checkbox-icon"
                      />
                    </div>
                    <div className="recap-link-item-content">
                      <div className="recap-link-item-header">
                        <span className="recap-link-item-number">
                          {recap.transaction_number}
                        </span>
                        {recap.recap_display_name && (
                          <span className="recap-link-item-transaction-id">
                            {recap.recap_display_name}
                          </span>
                        )}
                      </div>
                      <div className="recap-link-item-details">
                        {recap.vessel && (
                          <span className="recap-link-detail">Vessel: {recap.vessel}</span>
                        )}
                        {recap.quantity_mt && (
                          <span className="recap-link-detail">{recap.quantity_mt} MT</span>
                        )}
                        {(recap.load_port || recap.discharge_port) && (
                          <span className="recap-link-detail">
                            {recap.load_port || '?'} → {recap.discharge_port || '?'}
                          </span>
                        )}
                        {recap.laycan && (
                          <span className="recap-link-detail">Laycan: {recap.laycan}</span>
                        )}
                        {recap.charterer && (
                          <span className="recap-link-detail">Charterer: {recap.charterer}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="recap-link-modal-footer">
          <button type="button" className="recap-link-modal-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="recap-link-modal-btn-link"
            onClick={handleLink}
            disabled={selected.length === 0 || isLoading}
          >
            {isLoading
              ? 'Linking…'
              : `Link ${selected.length > 0 ? `(${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreightRecapLinkModal;
